import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import Modal from './Modal';
import ConfirmModal from '../common/ConfirmModal';
import { useConfirmModal } from '../../hooks/useConfirmModal';

const blinkAnimation = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 100px 20px 20px 20px;
  color: #e0e0e0;

  @media (max-width: 768px) {
    padding: 80px 15px 20px 15px;
  }
`;


const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    margin-bottom: 30px;
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #ff4444;
  margin-bottom: 10px;
  text-shadow: 0 0 10px rgba(255, 68, 68, 0.5);
  text-align: center;

  @media (max-width: 768px) {
    font-size: 2rem;
    margin-bottom: 15px;
  }
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #b0b0b0;
  margin: 0 0 40px 0;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.4;
    padding: 0 10px;
    margin-bottom: 30px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid rgba(255, 68, 68, 0.3);
  margin-bottom: 30px;
  overflow-x: auto;
  gap: 4px;
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
    gap: 2px;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  background: ${props => props.active ? 'rgba(255, 68, 68, 0.2)' : 'transparent'};
  border: none;
  color: ${props => props.active ? '#ff4444' : '#b0b0b0'};
  padding: 15px 25px;
  font-size: 14px;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  border-bottom: 3px solid ${props => props.active ? '#ff4444' : 'transparent'};
  white-space: nowrap;
  border-radius: 8px 8px 0 0;
  text-transform: uppercase;

  &:hover {
    color: #ff4444;
    background: rgba(255, 68, 68, 0.1);
  }

  @media (max-width: 768px) {
    padding: 12px 16px;
    font-size: 12px;
    flex: 1;
    min-width: 0;
  }
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  border-radius: 12px;
  backdrop-filter: blur(10px);
`;

const CreateButton = styled.button`
  background: linear-gradient(135deg, #ff4444 0%, #cc3333 100%);
  border: none;
  color: white;
  font-size: 14px;
  font-weight: 600;
  padding: 12px 24px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 68, 68, 0.3);
  text-transform: uppercase;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 68, 68, 0.4);
  }
`;

const FilterContainer = styled.div`
  display: flex;
  gap: 15px;
  align-items: center;
  flex-wrap: wrap;
`;

const FilterSelect = styled.select`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  color: #e0e0e0;
  font-size: 14px;
  padding: 10px 15px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #ff4444;
    box-shadow: 0 0 10px rgba(255, 68, 68, 0.3);
  }
  
  option {
    background: #1a1a1a;
    color: #e0e0e0;
  }
`;

const AchievementTable = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 68, 68, 0.2);
  border-radius: 12px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
`;

const TableHeader = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 2fr 1fr 1fr 1fr 1fr 120px;
  gap: 15px;
  padding: 20px;
  background: linear-gradient(135deg, rgba(255, 68, 68, 0.2) 0%, rgba(204, 51, 51, 0.2) 100%);
  border-bottom: 1px solid rgba(255, 68, 68, 0.2);
  font-weight: 600;
  text-transform: uppercase;
  color: #ff4444;
  font-size: 14px;
  letter-spacing: 0.5px;
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: 60px 1fr 2fr 1fr 1fr 1fr 1fr 120px;
  gap: 15px;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(255, 68, 68, 0.1);
  color: #e0e0e0;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 68, 68, 0.1);
    transform: translateY(-1px);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 10px;
  text-transform: uppercase;
  background: ${props => {
    switch (props.status) {
      case 'active': return 'rgba(0, 128, 0, 0.3)';
      case 'inactive': return 'rgba(128, 0, 0, 0.3)';
      default: return 'rgba(128, 128, 0, 0.3)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'active': return '#00cc00';
      case 'inactive': return '#cc0000';
      default: return '#cccc00';
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case 'active': return '#00cc00';
      case 'inactive': return '#cc0000';
      default: return '#cccc00';
    }
  }};
`;

const ActionButton = styled.button<{ variant?: 'edit' | 'delete' | 'toggle' }>`
  background: ${props => {
    switch (props.variant) {
      case 'edit': return 'linear-gradient(135deg, #ffa500 0%, #e6940a 100%)';
      case 'delete': return 'linear-gradient(135deg, #ff4444 0%, #cc3333 100%)';
      case 'toggle': return 'linear-gradient(135deg, #44a6ff 0%, #3388cc 100%)';
      default: return 'linear-gradient(135deg, #ff4444 0%, #cc3333 100%)';
    }
  }};
  border: none;
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 12px;
  margin: 0 4px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const FormContainer = styled.div`
  background: rgba(0, 0, 0, 0.9);
  padding: 20px;
  border: 1px solid #cc3000;
  font-family: 'Courier New', monospace;
  color: #ffffff;
`;

const FormGroup = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 5px;
  color: #cc3000;
  font-size: 12px;
  text-transform: uppercase;
`;

const Input = styled.input`
  width: 100%;
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid #cc3000;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 8px;
  
  &:focus {
    outline: none;
    border-color: #ff3000;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 60px;
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid #cc3000;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 8px;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: #ff3000;
  }
`;

const Select = styled.select`
  width: 100%;
  background: rgba(0, 0, 0, 0.8);
  border: 1px solid #cc3000;
  color: #ffffff;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 8px;
  
  &:focus {
    outline: none;
    border-color: #ff3000;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const IconPreviewContainer = styled.div`
  margin-top: 10px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const IconPreview = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #cc3000;
  object-fit: cover;
  background: rgba(0, 0, 0, 0.8);
`;

const IconPreviewPlaceholder = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px dashed #cc3000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #cc3000;
  background: rgba(0, 0, 0, 0.8);
`;

const IconPreviewText = styled.span`
  font-size: 10px;
  color: #999;
`;

const TableIcon = styled.img`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px solid #cc3000;
  object-fit: cover;
  background: rgba(0, 0, 0, 0.8);
`;

const TableIconPlaceholder = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 1px dashed #cc3000;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #cc3000;
  background: rgba(0, 0, 0, 0.8);
`;

const SubmitButton = styled.button`
  background: rgba(0, 128, 0, 0.3);
  border: 1px solid #00cc00;
  color: #00cc00;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 10px 20px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 128, 0, 0.5);
  }
`;

const CancelButton = styled.button`
  background: rgba(128, 0, 0, 0.3);
  border: 1px solid #cc0000;
  color: #cc0000;
  font-family: 'Courier New', monospace;
  font-size: 12px;
  padding: 10px 20px;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(128, 0, 0, 0.5);
  }
`;

interface Achievement {
  id: string;
  name: string;
  description: string;
  gameType: string;
  rarity: string;
  isActive: boolean;
  conditions: {
    type: string;
    count: number;
    missionId?: string;
    consecutively?: boolean;
    timeframe?: string;
    requiredAchievementId?: string;
  };
  iconUrl?: string;
  iconFile?: File;
  createdAt: string;
  updatedAt: string;
}

interface NewAchievement {
  name: string;
  description: string;
  gameType: string;
  rarity: string;
  conditions: {
    type: string;
    count: number;
    missionId?: string;
    consecutively?: boolean;
    timeframe?: string;
    requiredAchievementId?: string;
  };
  iconUrl?: string;
  iconFile?: File;
}

const AchievementManagement: React.FC = () => {
  const { confirm, modalProps } = useConfirmModal();
  const [activeTab, setActiveTab] = useState<'user' | 'clan' | 'referee'>('user');
  const [achievements, setAchievements] = useState<Achievement[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAchievement, setEditingAchievement] = useState<Achievement | null>(null);
  const [filterGameType, setFilterGameType] = useState<string>('all');
  const [filterRarity, setFilterRarity] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('all');

  const [newAchievement, setNewAchievement] = useState<NewAchievement>({
    name: '',
    description: '',
    gameType: 'any',
    rarity: 'common',
    conditions: {
      type: 'win_games',
      count: 1
    }
  });

  const achievementTypes = {
    user: [
      'win_games', 'play_games', 'win_mission', 'play_mission', 'mission_objective',
      'win_games_streak', 'play_games_streak', 'daily_games', 'weekly_games', 'daily_wins',
      'complete_all_missions', 'complete_all_objectives'
    ],
    clan: [
      'clan_win_games', 'clan_play_games', 'clan_members_count', 'clan_all_members_achievement'
    ],
    referee: [
      'referee_games', 'referee_missions', 'referee_experience',
      'referee_games_streak', 'referee_daily_games', 'referee_weekly_games', 'referee_all_missions'
    ]
  };

  const gameTypes = ['any', 'airsoft', 'paintball', 'speedball'];
  const rarities = ['common', 'uncommon', 'rare', 'epic', 'legendary'];

  useEffect(() => {
    fetchAchievements();
  }, [activeTab]);

  const fetchAchievements = async () => {
    setLoading(true);
    try {
      let data: Achievement[] = [];
      
      switch (activeTab) {
        case 'user':
          const { getUserAchievements } = await import('../../services/achievementService');
          data = await getUserAchievements();
          break;
        case 'clan':
          const { getClanAchievements } = await import('../../services/achievementService');
          data = await getClanAchievements();
          break;
        case 'referee':
          const { getRefereeAchievements } = await import('../../services/achievementService');
          data = await getRefereeAchievements();
          break;
      }
      
      setAchievements(data);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      const formData = new FormData();
      formData.append('name', newAchievement.name);
      formData.append('description', newAchievement.description);
      formData.append('gameType', newAchievement.gameType);
      formData.append('rarity', newAchievement.rarity);
      formData.append('conditions.type', newAchievement.conditions.type);
      formData.append('conditions.count', newAchievement.conditions.count.toString());
      
      if (newAchievement.iconFile) {
        formData.append('iconFile', newAchievement.iconFile);
      }

      switch (activeTab) {
        case 'user':
          const { createUserAchievementWithFile } = await import('../../services/achievementService');
          await createUserAchievementWithFile(formData);
          break;
        case 'clan':
          const { createClanAchievementWithFile } = await import('../../services/achievementService');
          await createClanAchievementWithFile(formData);
          break;
        case 'referee':
          const { createRefereeAchievementWithFile } = await import('../../services/achievementService');
          await createRefereeAchievementWithFile(formData);
          break;
      }

      setShowCreateModal(false);
      resetForm();
      fetchAchievements();
    } catch (error) {
      console.error('Error creating achievement:', error);
    }
  };

  const handleEdit = async () => {
    if (!editingAchievement) return;

    try {
      const formData = new FormData();
      formData.append('name', editingAchievement.name);
      formData.append('description', editingAchievement.description);
      formData.append('gameType', editingAchievement.gameType);
      formData.append('rarity', editingAchievement.rarity);
      formData.append('conditions.type', editingAchievement.conditions.type);
      formData.append('conditions.count', editingAchievement.conditions.count.toString());
      
      if (editingAchievement.iconFile) {
        formData.append('iconFile', editingAchievement.iconFile);
      }

      switch (activeTab) {
        case 'user':
          const { updateUserAchievementWithFile } = await import('../../services/achievementService');
          await updateUserAchievementWithFile(editingAchievement.id, formData);
          break;
        case 'clan':
          const { updateClanAchievementWithFile } = await import('../../services/achievementService');
          await updateClanAchievementWithFile(editingAchievement.id, formData);
          break;
        case 'referee':
          const { updateRefereeAchievementWithFile } = await import('../../services/achievementService');
          await updateRefereeAchievementWithFile(editingAchievement.id, formData);
          break;
      }

      setEditingAchievement(null);
      fetchAchievements();
    } catch (error) {
      console.error('Error updating achievement:', error);
    }
  };

  const handleToggleStatus = async (achievement: Achievement) => {
    try {
      switch (activeTab) {
        case 'user':
          const { toggleUserAchievementStatus } = await import('../../services/achievementService');
          await toggleUserAchievementStatus(achievement.id, !achievement.isActive);
          break;
        case 'clan':
          const { toggleClanAchievementStatus } = await import('../../services/achievementService');
          await toggleClanAchievementStatus(achievement.id, !achievement.isActive);
          break;
        case 'referee':
          const { toggleRefereeAchievementStatus } = await import('../../services/achievementService');
          await toggleRefereeAchievementStatus(achievement.id, !achievement.isActive);
          break;
      }

      fetchAchievements();
    } catch (error) {
      console.error('Error toggling achievement status:', error);
    }
  };

  const handleDelete = async (achievement: Achievement) => {
    const confirmed = await confirm({
      title: 'Delete Achievement',
      message: `Are you sure you want to delete "${achievement.name}"?`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      switch (activeTab) {
        case 'user':
          const { deleteUserAchievement } = await import('../../services/achievementService');
          await deleteUserAchievement(achievement.id);
          break;
        case 'clan':
          const { deleteClanAchievement } = await import('../../services/achievementService');
          await deleteClanAchievement(achievement.id);
          break;
        case 'referee':
          const { deleteRefereeAchievement } = await import('../../services/achievementService');
          await deleteRefereeAchievement(achievement.id);
          break;
      }

      fetchAchievements();
    } catch (error) {
      console.error('Error deleting achievement:', error);
    }
  };

  const resetForm = () => {
    setNewAchievement({
      name: '',
      description: '',
      gameType: 'any',
      rarity: 'common',
      conditions: {
        type: achievementTypes[activeTab][0],
        count: 1
      }
    });
  };

  const filteredAchievements = achievements.filter(achievement => {
    if (filterGameType !== 'all' && achievement.gameType !== filterGameType) return false;
    if (filterRarity !== 'all' && achievement.rarity !== filterRarity) return false;
    if (filterStatus !== 'all') {
      const status = achievement.isActive ? 'active' : 'inactive';
      if (status !== filterStatus) return false;
    }
    return true;
  });

  const renderForm = (achievement: NewAchievement | Achievement, isEdit: boolean = false) => (
    <FormContainer>
      <FormGroup>
        <Label>Name</Label>
        <Input
          type="text"
          value={achievement.name}
          onChange={(e) => {
            if (isEdit) {
              setEditingAchievement(prev => prev ? { ...prev, name: e.target.value } : null);
            } else {
              setNewAchievement(prev => ({ ...prev, name: e.target.value }));
            }
          }}
          placeholder="Achievement name"
        />
      </FormGroup>

      <FormGroup>
        <Label>Description</Label>
        <TextArea
          value={achievement.description}
          onChange={(e) => {
            if (isEdit) {
              setEditingAchievement(prev => prev ? { ...prev, description: e.target.value } : null);
            } else {
              setNewAchievement(prev => ({ ...prev, description: e.target.value }));
            }
          }}
          placeholder="Achievement description"
        />
      </FormGroup>

      <FormGroup>
        <Label>Game Type</Label>
        <Select
          value={achievement.gameType}
          onChange={(e) => {
            if (isEdit) {
              setEditingAchievement(prev => prev ? { ...prev, gameType: e.target.value } : null);
            } else {
              setNewAchievement(prev => ({ ...prev, gameType: e.target.value }));
            }
          }}
        >
          {gameTypes.map(type => (
            <option key={type} value={type}>{type.toUpperCase()}</option>
          ))}
        </Select>
      </FormGroup>

      <FormGroup>
        <Label>Rarity</Label>
        <Select
          value={achievement.rarity}
          onChange={(e) => {
            if (isEdit) {
              setEditingAchievement(prev => prev ? { ...prev, rarity: e.target.value } : null);
            } else {
              setNewAchievement(prev => ({ ...prev, rarity: e.target.value }));
            }
          }}
        >
          {rarities.map(rarity => (
            <option key={rarity} value={rarity}>{rarity.toUpperCase()}</option>
          ))}
        </Select>
      </FormGroup>

      <FormGroup>
        <Label>Achievement Type</Label>
        <Select
          value={achievement.conditions.type}
          onChange={(e) => {
            if (isEdit) {
              setEditingAchievement(prev => prev ? {
                ...prev,
                conditions: { ...prev.conditions, type: e.target.value }
              } : null);
            } else {
              setNewAchievement(prev => ({
                ...prev,
                conditions: { ...prev.conditions, type: e.target.value }
              }));
            }
          }}
        >
          {achievementTypes[activeTab].map(type => (
            <option key={type} value={type}>{type.toUpperCase().replace(/_/g, ' ')}</option>
          ))}
        </Select>
      </FormGroup>

      <FormGroup>
        <Label>Count</Label>
        <Input
          type="number"
          min="1"
          value={achievement.conditions.count}
          onChange={(e) => {
            const count = parseInt(e.target.value) || 1;
            if (isEdit) {
              setEditingAchievement(prev => prev ? {
                ...prev,
                conditions: { ...prev.conditions, count }
              } : null);
            } else {
              setNewAchievement(prev => ({
                ...prev,
                conditions: { ...prev.conditions, count }
              }));
            }
          }}
        />
      </FormGroup>

      <FormGroup>
        <Label>Icon File (Optional)</Label>
        <Input
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) {
              if (isEdit) {
                setEditingAchievement(prev => prev ? { ...prev, iconFile: file } : null);
              } else {
                setNewAchievement(prev => ({ ...prev, iconFile: file }));
              }
            }
          }}
        />
        <IconPreviewContainer>
          {achievement.iconUrl ? (
            <IconPreview 
              src={achievement.iconUrl} 
              alt="Achievement Icon"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const nextSibling = e.currentTarget.nextElementSibling as HTMLElement;
                if (nextSibling) nextSibling.style.display = 'flex';
              }}
            />
          ) : (
            <IconPreviewPlaceholder>
              🏆
            </IconPreviewPlaceholder>
          )}
          <IconPreviewText>
            {achievement.iconUrl ? 'Icon Preview' : 'No icon file uploaded'}
          </IconPreviewText>
        </IconPreviewContainer>
      </FormGroup>

      <ButtonGroup>
        <SubmitButton onClick={isEdit ? handleEdit : handleCreate}>
          {isEdit ? 'UPDATE' : 'CREATE'}
        </SubmitButton>
        <CancelButton onClick={() => {
          if (isEdit) {
            setEditingAchievement(null);
          } else {
            setShowCreateModal(false);
            resetForm();
          }
        }}>
          CANCEL
        </CancelButton>
      </ButtonGroup>
    </FormContainer>
  );

  return (
    <Container>
      <Header>
        <Title>Achievement Management</Title>
        <Subtitle>Manage user, clan, and referee achievements</Subtitle>
      </Header>

      <TabContainer>
        <Tab active={activeTab === 'user'} onClick={() => setActiveTab('user')}>
          USER ACHIEVEMENTS
        </Tab>
        <Tab active={activeTab === 'clan'} onClick={() => setActiveTab('clan')}>
          CLAN ACHIEVEMENTS
        </Tab>
        <Tab active={activeTab === 'referee'} onClick={() => setActiveTab('referee')}>
          REFEREE ACHIEVEMENTS
        </Tab>
      </TabContainer>

      <ActionBar>
        <CreateButton onClick={() => {
          resetForm();
          setShowCreateModal(true);
        }}>
          + CREATE NEW ACHIEVEMENT
        </CreateButton>

        <FilterContainer>
          <FilterSelect value={filterGameType} onChange={(e) => setFilterGameType(e.target.value)}>
            <option value="all">ALL GAME TYPES</option>
            {gameTypes.map(type => (
              <option key={type} value={type}>{type.toUpperCase()}</option>
            ))}
          </FilterSelect>

          <FilterSelect value={filterRarity} onChange={(e) => setFilterRarity(e.target.value)}>
            <option value="all">ALL RARITIES</option>
            {rarities.map(rarity => (
              <option key={rarity} value={rarity}>{rarity.toUpperCase()}</option>
            ))}
          </FilterSelect>

          <FilterSelect value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">ALL STATUS</option>
            <option value="active">ACTIVE</option>
            <option value="inactive">INACTIVE</option>
          </FilterSelect>
        </FilterContainer>
      </ActionBar>

      <AchievementTable>
        <TableHeader>
          <div>ICON</div>
          <div>NAME</div>
          <div>DESCRIPTION</div>
          <div>GAME TYPE</div>
          <div>RARITY</div>
          <div>TYPE</div>
          <div>STATUS</div>
          <div>ACTIONS</div>
        </TableHeader>

        {loading ? (
          <TableRow>
            <div style={{ gridColumn: '1 / -1', textAlign: 'center' }}>LOADING...</div>
          </TableRow>
        ) : filteredAchievements.length === 0 ? (
          <TableRow>
            <div style={{ gridColumn: '1 / -1', textAlign: 'center' }}>NO ACHIEVEMENTS FOUND</div>
          </TableRow>
        ) : (
          filteredAchievements.map(achievement => (
            <TableRow key={achievement.id}>
              <div>
                {achievement.iconUrl ? (
                  <TableIcon 
                    src={achievement.iconUrl} 
                    alt={achievement.name}
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const placeholder = e.currentTarget.parentNode?.querySelector('.icon-placeholder') as HTMLElement;
                      if (placeholder) placeholder.style.display = 'flex';
                    }}
                  />
                ) : (
                  <TableIconPlaceholder className="icon-placeholder">
                    🏆
                  </TableIconPlaceholder>
                )}
              </div>
              <div>{achievement.name}</div>
              <div>{achievement.description}</div>
              <div>{achievement.gameType.toUpperCase()}</div>
              <div>{achievement.rarity.toUpperCase()}</div>
              <div>{achievement.conditions.type.replace(/_/g, ' ').toUpperCase()}</div>
              <div>
                <StatusBadge status={achievement.isActive ? 'active' : 'inactive'}>
                  {achievement.isActive ? 'ACTIVE' : 'INACTIVE'}
                </StatusBadge>
              </div>
              <div>
                <ActionButton
                  variant="edit"
                  onClick={() => setEditingAchievement(achievement)}
                >
                  EDIT
                </ActionButton>
                <ActionButton
                  variant="toggle"
                  onClick={() => handleToggleStatus(achievement)}
                >
                  {achievement.isActive ? 'DISABLE' : 'ENABLE'}
                </ActionButton>
                <ActionButton
                  variant="delete"
                  onClick={() => handleDelete(achievement)}
                >
                  DELETE
                </ActionButton>
              </div>
            </TableRow>
          ))
        )}
      </AchievementTable>

      <Modal 
        isOpen={showCreateModal} 
        onClose={() => setShowCreateModal(false)}
        title={`Create New ${activeTab.toUpperCase()} Achievement`}
      >
        {renderForm(newAchievement)}
      </Modal>

      <Modal
        isOpen={!!editingAchievement}
        onClose={() => setEditingAchievement(null)}
        title={`Edit ${activeTab.toUpperCase()} Achievement`}
      >
        {editingAchievement && renderForm(editingAchievement, true)}
      </Modal>
      <ConfirmModal {...modalProps} />
    </Container>
  );
};

export default AchievementManagement;